import React from 'react'
import { gql } from '@apollo/client'
import map from 'lodash/map'
import type { Project, RouteDef } from '@ztechapps/common.contract'
import { useDocument } from '@ztechapps/common.hook'
import { useInject, WebuiIocKey } from '@ztechapps/webui.contract'
import { LocalDate, LocalDateTime } from '@ztechapps/webui.date'
import { EntityCodeLink, EntityPreview } from '@ztechapps/webui.util'
import { ProjectStatus, ProjectStatusBadge } from '../project-status/ProjectStatus'

const projectPreviewQuery = gql`
  query singleProject($filter: JSON) {
    singleProject(filter: $filter) {
      _id
      code
      status
      name
      createdAt
      completionDate
      tasks {
        _id
        name
      }
    }
  }
`

const ProjectPreview = ({ _id, code }: { _id: string; code: string }) => {
  const projectDocumentResult = useDocument<Project>({ _id, query: projectPreviewQuery })
  const project = projectDocumentResult.document

  return (
    <EntityPreview
      code={code}
      statusBadge={<ProjectStatusBadge status={project?.status} />}
      loading={projectDocumentResult.loading}
      title={project?.name}
      rows={[
        // `LocalDate`/`LocalDateTime` always render SOMETHING (their own "--" for a missing
        // date), so `EntityPreview`'s `?? '---'` fallback never sees a falsy value to catch —
        // checked here instead, same as `WorkOrderCodeLink`'s Tags row.
        { label: 'Date Added', value: project?.createdAt ? <LocalDateTime value={project?.createdAt} /> : undefined },
        {
          label: 'Completion Date',
          value: project?.completionDate ? <LocalDate value={project?.completionDate} /> : undefined,
        },
      ]}
      list={{ label: 'Tasks', items: map(project?.tasks, (task) => task?.name) }}
    />
  )
}

export type ProjectCodeLinkProps = { _id: string; code: string; status: string }

export const ProjectCodeLink = ({ _id, code, status }: ProjectCodeLinkProps) => {
  const projectItemRoute = useInject<RouteDef>(WebuiIocKey.routes.projectItem)

  return (
    <EntityCodeLink
      code={code}
      statusDot={<ProjectStatus status={status} />}
      to={projectItemRoute.altPath({ _id })}
      target="_blank"
      preview={<ProjectPreview _id={_id} code={code} />}
    />
  )
}
